/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ICoreContext } from '@msdyn365-commerce/core';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IAddressAddItem, IAddressAddUpdateProps } from '@msdyn365-commerce-modules/address';
import { IBusinessAccountAddressViewProps } from '@msdyn365-commerce-modules/address';

import { debounce } from 'lodash';
import { searchAddressAsync } from '../../../actions/search-address';
import { IKartverketAddress } from '../../../actions/entities/kartverket-address';

let controller = new AbortController();
const onDoAutoComplete = debounce(async (
    context: ICoreContext,
    signal: AbortSignal,
    searchText: string,
    setIsSearching: (value: React.SetStateAction<boolean>) => void,
    setAutocompleteResults: (value: React.SetStateAction<IKartverketAddress[]>) => void,
    setShowAutoComplete: (value: React.SetStateAction<boolean>) => void
) => {
    if (searchText === '') {
        setAutocompleteResults([]);
        return;
    }

    if (signal.aborted) {
        // Prime it for the next search
        controller = new AbortController();
        return;
    }

    setIsSearching(true);
    setShowAutoComplete(false);

    try {
        const result = await searchAddressAsync({ callerContext: context.actionContext }, searchText);

        if (signal.aborted) {
            // Prime it for the next search
            controller = new AbortController();
            return;
        }

        setAutocompleteResults(result);
        setShowAutoComplete(true);
    } catch {
    } finally {
        setIsSearching(false);
    }
}, 500);

export interface IAddressAddUpdatePropsExt extends IAddressAddUpdateProps {
    context: ICoreContext;
    dontShowRequiredMark?: boolean;
    hideLabels?: boolean;
    labelsAsPlaceholders?: boolean;
}

/**
 * Address Add Update Component.
 * @param param0 - Root param.
 * @param param0.AddressForm - Address form.
 * @param param0.heading - Address Heading.
 * @param param0.items - IAddressAddItem[].
 * @param param0.hasError - Boolean.
 * @param param0.error - IAddressError.
 * @param param0.isShowSaveButton - Boolean.
 * @param param0.saveButton - Save button.
 * @param param0.isShowCancelButton - Boolean.
 * @param param0.cancelButton - Cancel button.
 * @returns - AddressForm Node.
 */
const AddressAddUpdate: React.FC<IAddressAddUpdatePropsExt> = ({
    context,
    AddressForm,
    heading,
    items,
    hasError,
    error,
    isShowSaveButton,
    saveButton,
    isShowCancelButton,
    cancelButton,
    dontShowRequiredMark,
    hideLabels,
    labelsAsPlaceholders
}) => {
    const [autocompleteResults, setAutocompleteResults] = React.useState<IKartverketAddress[]>([]);
    const [showAutoComplete, setShowAutoComplete] = React.useState<boolean>(false);
    const [isSearching, setIsSearching] = React.useState<boolean>(false);

    React.useEffect(() => {
        const streetInput = document.querySelector('#company_addressstreet') as HTMLInputElement;

        const onStreetInputClickHandler = () => {
            if (autocompleteResults.length > 0) {
                setShowAutoComplete(true);
            }
        };

        // Track whether the street input or auto complete list lost focus by clicking outside
        const onDocumentClickHandler = (e: MouseEvent) => {
            const streetAutoComplete = document.querySelector('.msc-address-form__auto-complete') as HTMLUListElement | null;

            if (e.target !== streetInput && !streetAutoComplete?.contains(e.target as Node)) {
                setShowAutoComplete(false);
            }
        };

        document.addEventListener('click', onDocumentClickHandler);

        const onKeyDownHandler = (e: KeyboardEvent) => {
            if (e.key === 'Tab' || e.key === 'Escape') {
                controller?.abort();
                setShowAutoComplete(false);
            }
            if (e.key === 'ArrowDown') {
                e.preventDefault();

                if (!showAutoComplete) {
                    if (autocompleteResults.length > 0) {
                        setShowAutoComplete(true);
                    }
                } else {
                    const streetAutoComplete = document.querySelector('.msc-address-form__auto-complete') as HTMLUListElement | null;
                    const firstItem = streetAutoComplete?.querySelector('li');

                    firstItem?.focus();
                }
            }
        };

        streetInput?.setAttribute('autocomplete', 'none');
        streetInput?.addEventListener('click', onStreetInputClickHandler);
        streetInput?.addEventListener('keydown', onKeyDownHandler);

        return () => {
            document.removeEventListener('click', onDocumentClickHandler);
            streetInput?.removeEventListener('click', onStreetInputClickHandler);
            streetInput?.removeEventListener('keydown', onKeyDownHandler);
        };
    }, [items, showAutoComplete, autocompleteResults]);

    return (
        <Node {...AddressForm}>
            {heading}
            {items.map((item: IAddressAddItem) => {
                const { AddressItem, key, alert } = item;

                const label = React.cloneElement(item.label as React.ReactElement, {
                    text: (item.label as JSX.Element).props.text?.endsWith('*') || dontShowRequiredMark
                        ? (item.label as JSX.Element).props.text
                        : `${(item.label as JSX.Element).props.text} *`
                });

                let input = item.input as JSX.Element | undefined;

                if (input && key === 'Street') {
                    const orgOnChange: (event: React.ChangeEvent<HTMLInputElement>) => void = input.props.onChange;

                    input = React.cloneElement(input, {
                        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                            e.persist();
                            const preventAutoCompleteSearch = !!(e.nativeEvent as CustomEvent).detail?.preventAutoCompleteSearch;
                            orgOnChange?.(e);

                            if (!preventAutoCompleteSearch) {
                                onDoAutoComplete(
                                    context,
                                    controller.signal,
                                    e.target.value,
                                    setIsSearching,
                                    setAutocompleteResults,
                                    setShowAutoComplete);
                            }
                        },
                        onChangeOriginal: orgOnChange,
                        additionalAddributes: {
                            required: true,
                            'aria-required': true,
                            placeholder: labelsAsPlaceholders ? label.props.text : undefined
                        }
                    });
                } else if (input) {
                    input = React.cloneElement(input as React.ReactElement, {
                        additionalAddributes: {
                            required: true,
                            'aria-required': true,
                            placeholder: labelsAsPlaceholders ? label.props.text : undefined
                        }
                    });
                }

                return (
                    <Node {...AddressItem} key={key}>
                        {!hideLabels && label}
                        {hasError && alert}
                        {key === 'Street' && isSearching && (
                            <span className={`${AddressForm.className}__input-spinner`} />
                        )}
                        {key === 'Street' && !isSearching && (
                            <button className={`${AddressForm.className}__input-search-btn`} tabIndex={-1} />
                        )}
                        {input}
                        {key === 'Street' && autocompleteResults.length > 0 && (
                            <ul
                                className={`${AddressForm.className}__auto-complete`}
                                style={{ display: showAutoComplete ? 'block' : 'none' }}
                            >
                                {autocompleteResults.map((result) => {
                                    const onClickHandler = () => {
                                        const onChangeOriginal = (input as JSX.Element).props.onChangeOriginal;

                                        // Fake event calls to update the address state
                                        onChangeOriginal?.({
                                            target: {
                                                name: 'Street',
                                                value: result.Street
                                            } as unknown as EventTarget as HTMLInputElement
                                        } as React.ChangeEvent<HTMLInputElement>);

                                        onChangeOriginal?.({
                                            target: {
                                                name: 'ZipCode',
                                                value: result.ZipCode
                                            } as unknown as EventTarget as HTMLInputElement
                                        } as React.ChangeEvent<HTMLInputElement>);

                                        onChangeOriginal?.({
                                            target: {
                                                name: 'City',
                                                value: result.City
                                            } as unknown as EventTarget as HTMLInputElement
                                        } as React.ChangeEvent<HTMLInputElement>);

                                        setShowAutoComplete(false);
                                    };
                                    return (
                                        <li
                                            key={`${result.Street}, ${result.ZipCode} ${result.City}`}
                                            onClick={onClickHandler}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    onClickHandler();
                                                }
                                                if (e.key === 'Tab') {
                                                    setShowAutoComplete(false);
                                                }
                                                if (e.key === 'Escape') {
                                                    setShowAutoComplete(false);
                                                    const streetInput = document.querySelector('#company_addressstreet') as HTMLInputElement;
                                                    streetInput?.focus();
                                                }
                                                if (e.key === 'ArrowDown') {
                                                    e.preventDefault();
                                                    const nextItem = e.currentTarget.nextElementSibling as HTMLLIElement | null;
                                                    nextItem?.focus();
                                                }
                                                if (e.key === 'ArrowUp') {
                                                    e.preventDefault();
                                                    const prevItem = e.currentTarget.previousElementSibling as HTMLLIElement | null;

                                                    if (!prevItem) {
                                                        const streetInput = document.querySelector('#company_addressstreet') as HTMLInputElement;
                                                        streetInput?.focus();
                                                    } else {
                                                        prevItem?.focus();
                                                    }
                                                }
                                            }}
                                            tabIndex={-1}
                                        >
                                            {result.Street}, {result.ZipCode} {result.City}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </Node>
                );
            })}
            {hasError && (
                <Node {...error.AddressError}>
                    {error.title}
                    {error.message}
                </Node>
            )}
            {isShowSaveButton && saveButton}
            {isShowCancelButton && cancelButton}
        </Node>
    );
};

/**
 * Business Account Address View Component.
 * @param props - Props.
 * @returns - BusinessAccountAddress Module.
 */
const BusinessAccountAddressView: React.FC<IBusinessAccountAddressViewProps & {
    dontShowRequiredMark?: boolean,
    hideLabels?: boolean,
    labelsAsPlaceholders?: boolean
}> = props => {
    const { BusinessAccountAddress, showAddOrUpdateAddress } = props;

    return (
        <Module {...BusinessAccountAddress}>
            <AddressAddUpdate
                {...showAddOrUpdateAddress}
                context={props.BusinessAccountAddress.moduleProps.context}
                dontShowRequiredMark={props.dontShowRequiredMark}
                hideLabels={props.hideLabels}
                labelsAsPlaceholders={props.labelsAsPlaceholders}
            />
        </Module>
    );
};

export default BusinessAccountAddressView;
